import { useState, useEffect } from 'react';
import { Typography } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom-v5-compat';
import { UnauthenticatedLayout } from '~/layout/UnauthenticatedLayout';
import { Flex } from '~/components/Flex';

const { Title, Paragraph, Text, Link } = Typography;

const GCP_TOKEN_QUERY_PARAM = 'token';
const GCP_PROCUREMENT_ACCOUNT_ID_QUERY_PARAM = 'procurementAccountId';

type GcpSignupState = 'checking' | 'received' | 'error';

export const GcpSignup = () => {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<GcpSignupState>('checking');
  const [procurementAccountId, setProcurementAccountId] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get(GCP_TOKEN_QUERY_PARAM);
    if (!token) {
      setState('error');
      return;
    }
    setProcurementAccountId(searchParams.get(GCP_PROCUREMENT_ACCOUNT_ID_QUERY_PARAM));
    setState('received');
  }, [searchParams]);

  if (state === 'checking') {
    return (
      <UnauthenticatedLayout bodyStyle={{ textAlign: 'center' }}>
        <Flex direction="vertical" gap="lg">
          <LoadingOutlined style={{ fontSize: 48 }} spin />
          <Title level={4} style={{ margin: 0 }}>
            Processing...
          </Title>
        </Flex>
      </UnauthenticatedLayout>
    );
  }

  if (state === 'error') {
    return (
      <UnauthenticatedLayout bodyStyle={{ textAlign: 'center' }}>
        <Flex direction="vertical" gap="md">
          <CloseCircleOutlined style={{ fontSize: '48px', color: '#ff4d4f' }} />
          <Title level={3} style={{ margin: 0 }}>
            Invalid Signup Link
          </Title>
          <Paragraph>No token found. Please try again from GCP Marketplace.</Paragraph>
          <Paragraph>
            <Text type="secondary">Need help? Email </Text>
            <Link href="mailto:support@shipium.com">support@shipium.com</Link>
          </Paragraph>
        </Flex>
      </UnauthenticatedLayout>
    );
  }

  return (
    <UnauthenticatedLayout bodyStyle={{ textAlign: 'center' }}>
      <Flex direction="vertical" gap="md">
        <CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a' }} />
        <Title level={3} style={{ margin: 0 }}>
          Subscription Received
        </Title>
        <Paragraph>
          Thank you for subscribing to Shipium via GCP Marketplace! Your subscription has been received and a member of
          our team will be in touch shortly to complete your account setup.
        </Paragraph>
        {procurementAccountId && (
          <Paragraph>
            <Text type="secondary">Reference: </Text>
            <Text copyable>{procurementAccountId}</Text>
          </Paragraph>
        )}
        <Paragraph>
          <Text type="secondary">Questions? Email </Text>
          <Link href="mailto:sales@shipium.com">sales@shipium.com</Link>
        </Paragraph>
      </Flex>
    </UnauthenticatedLayout>
  );
};

export default GcpSignup;
